/** Shared by lab and med */

import { StateCreator } from "zustand";
import { produce } from "immer";
import { ConnectStoreUnion } from ".";
import { supabase, Tables } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import {
	SupabaseTableEnum,
	SupabaseWriteTableEnum,
} from "@/lib/supabase/supabaseTypes";
import posthog from "posthog-js";
import { useCentralStore } from "../Central";

export type VendorBrand = Omit<
	Tables<"brands">,
	"standard_form_published" | "legacy_standard_form_published"
> & {
	admin_email?: string | null;
};

interface ConnectState {
	connectedVendors: string[];
	brandsByOrgId: Record<string, VendorBrand>;
}

export const initialConnectState: ConnectState = {
	connectedVendors: [],
	brandsByOrgId: {},
};

export interface ConnectStoreSlice extends ConnectState {
	// "connectedLabs" act as vendors; we only need connected labs because
	// we only want to submit forms for vendors
	fetchConnectedVendors: (myOrgId: string) => Promise<void>;
	fetchBrands: (orgId: string) => Promise<void>;
	connectVendeeAsClient: (
		otherOrgId: string,
		clientId: string
	) => Promise<boolean>;
}

export const createConnectStore: StateCreator<
	ConnectStoreUnion,
	[],
	[],
	ConnectStoreSlice
> = (set, get) => ({
	...initialConnectState,
	fetchConnectedVendors: async (myOrgId: string) => {
		const { data, error } = await supabase.rpc("get_connected_vendors", {
			my_org_id: myOrgId,
		});

		if (error) {
			Logger.error(error.message);
		}

		const connectedVendors = Array.from(
			new Set(
				(data ?? [])
					.map((row) => row.org_id)
					.filter((orgId) => orgId && orgId !== myOrgId)
			)
		);

		const brandsByOrgId = (data ?? []).reduce(
			(acc, row) => {
				if (!row.organization_id || !row.id || !row.name) {
					return acc;
				}

				acc[row.organization_id] = {
					id: row.id,
					created_at: row.created_at,
					modified_at: row.modified_at,
					name: row.name,
					organization_id: row.organization_id,
					admin_email: row.admin_email ?? null,
				};
				return acc;
			},
			{} as Record<string, VendorBrand>
		);

		set(
			produce((state) => {
				state.connectedVendors = connectedVendors;
				state.brandsByOrgId = {
					...state.brandsByOrgId,
					...(brandsByOrgId ?? {}),
				};
			})
		);
	},
	connectVendeeAsClient: async (otherOrgId: string, clientId: string) => {
		if (!otherOrgId || !clientId) {
			Logger.error(
				`otherOrgId or clientId is not set: ${otherOrgId} ${clientId}`
			);
			return false;
		}

		const { error: connectClientError } = await supabase
			.from(SupabaseWriteTableEnum.CLIENTS)
			.update({
				connected_organization_id: otherOrgId,
			})
			.eq("id", clientId);

		if (connectClientError) {
			Logger.error(connectClientError.message);
			return false;
		}

		posthog.capture("connection_established", {
			connection_type: "existing_org",
			initiated_by: "customer",
		});
		const orgId = useCentralStore.getState().organization?.id;
		if (orgId) {
			posthog.group("organization", orgId, {
				$set_once: { first_connection_at: new Date().toISOString() },
			});
		}
		posthog.capture("client_connected_to_dentconnect", {
			client_id: clientId,
			is_new_connection: true,
		});

		return true;
	},
	fetchBrands: async (orgId: string) => {
		const { data, error } = await supabase.rpc("get_brands", {
			p_org_id: orgId,
		});

		if (error) {
			Logger.error(error.message);
		}

		const existingBrandsByOrgId = get().brandsByOrgId;
		const brandsByOrgId = (data ?? []).reduce(
			(acc, row) => {
				acc[row.organization_id] = {
					...row,
					admin_email:
						existingBrandsByOrgId[row.organization_id]
							?.admin_email ?? null,
				};
				return acc;
			},
			{} as Record<string, VendorBrand>
		);

		set(
			produce((state) => {
				state.brandsByOrgId = brandsByOrgId;
			})
		);
	},
});
